import { faChevronDown, faChevronUp, faSadTear } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { Loader } from '@/components'
import type { SearchFilterPresetKey } from '@/components/NavBar/SearchTypeFilter/SearchTypeFilter'
import {
	PRESET_I18N_KEYS,
	PRESET_OPTIONS,
} from '@/components/NavBar/SearchTypeFilter/SearchTypeFilter'
import { useAppContext } from '@/hooks'
import { FullTextSearchDto } from '@/rtkApi/mddApiSliceGenerated'

import { Result } from './Result'

type ResultsProps = {
	hasMore?: boolean
	loading: boolean
	loadingMore?: boolean
	onFilterPresetToggle: React.Dispatch<
		React.SetStateAction<SearchFilterPresetKey[]>
	>
	onLoadMore?: () => void
	results: FullTextSearchDto[] | undefined
	selectedPresets: SearchFilterPresetKey[]
}

export const Results = ({
	results,
	loading,
	loadingMore = false,
	hasMore = false,
	onLoadMore,
	selectedPresets,
	onFilterPresetToggle,
}: ResultsProps) => {
	const { t } = useAppContext()
	const [filterExpanded, setFilterExpanded] = useState(false)

	const FILTER_FIRST_ROW = 5
	const displayPresets = PRESET_OPTIONS.filter(
		(p): p is SearchFilterPresetKey => p !== 'all',
	)
	const firstRowPresets = displayPresets.slice(0, FILTER_FIRST_ROW)
	const morePresets = displayPresets.slice(FILTER_FIRST_ROW)

	const handlePresetClick = (preset: SearchFilterPresetKey) => {
		onFilterPresetToggle((prev) =>
			prev.includes(preset)
				? prev.filter((p) => p !== preset)
				: [...prev, preset],
		)
	}

	return (
		<ResultsWrapper>
			<FilterSection>
				<FilterRow>
					{firstRowPresets.map((preset) => (
						<FilterOption
							key={preset}
							$active={selectedPresets.includes(preset)}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => handlePresetClick(preset)}
						>
							{t(PRESET_I18N_KEYS[preset] as Parameters<typeof t>[0])}
						</FilterOption>
					))}
				</FilterRow>
				{morePresets.length > 0 && (
					<>
						{filterExpanded && (
							<FilterRow $compact>
								{morePresets.map((preset) => (
									<FilterOption
										key={preset}
										$active={selectedPresets.includes(preset)}
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => handlePresetClick(preset)}
									>
										{t(PRESET_I18N_KEYS[preset] as Parameters<typeof t>[0])}
									</FilterOption>
								))}
							</FilterRow>
						)}
						<ShowMoreRow
							as="button"
							type="button"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => setFilterExpanded((e) => !e)}
						>
							{filterExpanded
								? t('NAVBAR_SEARCH_FILTER_SHOW_LESS' as Parameters<typeof t>[0])
								: t('NAVBAR_SEARCH_FILTER_SHOW_MORE' as Parameters<typeof t>[0])}
							<FontAwesomeIcon
								icon={filterExpanded ? faChevronUp : faChevronDown}
								style={{ marginLeft: '0.25rem' }}
							/>
						</ShowMoreRow>
					</>
				)}
			</FilterSection>
			<Loader loaded={!loading} />
			{!loading &&
				results &&
				results.map((r) => <Result key={r.id} item={r} />)}
			{hasMore && results && results.length > 0 && (
				<LoadMoreRow
					as="button"
					type="button"
					onMouseDown={(e) => e.preventDefault()}
					onClick={onLoadMore}
					disabled={loadingMore}
				>
					{loadingMore
						? t('NAVBAR_SEARCH_LOADING_MORE')
						: t('NAVBAR_SEARCH_LOAD_MORE')}
				</LoadMoreRow>
			)}
			{!loading && results && results.length === 0 && (
				<NoResults>
					<NoResultsIcon>
						<FontAwesomeIcon color="#999" icon={faSadTear} />
					</NoResultsIcon>
					No results found
				</NoResults>
			)}
		</ResultsWrapper>
	)
}

const ResultsWrapper = styled.div`
	position: absolute;
	top: 31px;
	left: 0;
	right: 0;
	background: #fff;
	z-index: 10;
	color: #000;
	max-height: 80vh;
	overflow: auto;

	${(props) => css`
		border: 1px solid ${props.theme.colors.layout.topBackgroundColor};
		border-top: 0;
	`}
`

const NoResults = styled.div`
	padding: 3rem 0;
	text-align: center;
`

const NoResultsIcon = styled.span`
	margin-right: 0.5rem;
`

const FilterSection = styled.div`
	background: #f5f5f5;
	border-bottom: 1px solid ${(props) => props.theme.colors.layout.topBackgroundColor ?? '#e0e0e0'};
`

const FilterRow = styled.div<{ $compact?: boolean }>`
	display: flex;
	flex-wrap: wrap;
	gap: 0.25rem;
	padding: ${(p) =>
		p.$compact ? '0.125rem 0.75rem 0.25rem' : '0.25rem 0.75rem 0.125rem'};
`

const ShowMoreRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 0.2rem 0.75rem 0.35rem;
	font-size: 0.75rem;
	color: #666;
	background: none;
	border: none;
	cursor: pointer;
	&:hover {
		color: #333;
		text-decoration: underline;
	}
`

const FilterOption = styled.button<{ $active: boolean }>`
	padding: 0.25rem 0.5rem;
	font-size: 0.8125rem;
	border: 1px solid #ccc;
	border-radius: 4px;
	background: ${(props) => (props.$active ? props.theme.colors?.button?.primary?.background ?? '#1976d2' : '#fff')};
	color: ${(props) => (props.$active ? '#fff' : '#333')};
	cursor: pointer;
	&:hover {
		background: ${(props) => (props.$active ? props.theme.colors?.button?.primary?.background ?? '#1976d2' : '#e8e8e8')};
	}
`

const LoadMoreRow = styled.div`
	display: block;
	width: 100%;
	padding: 0.5rem 0.75rem;
	text-align: center;
	font-size: 0.8125rem;
	color: ${(props) => props.theme.colors?.button?.primary?.background ?? '#1976d2'};
	background: none;
	border: none;
	border-top: 1px solid ${(props) => props.theme.colors?.layout?.topBackgroundColor ?? '#e0e0e0'};
	cursor: pointer;
	&:hover:not(:disabled) {
		text-decoration: underline;
	}
	&:disabled {
		color: #999;
		cursor: default;
	}
`
